/**
 * AMAÇ: Uygulamanın kök bileşeni — provider'ları sarar ve tüm rotaları tanımlar
 * Giriş gerektiren sayfalar ProtectedRoute + MainLayout altında toplanır
 *
 * Kod bölme: LandingPage (SEO'nun hedefi olan / , /en ...) dışındaki HER SAYFA
 * lazy import — girişsiz bir ziyaretçi yalnızca landing'i indirir, uygulamanın
 * tamamını (dashboard, istatistik, admin ...) değil. Vite build uyarısı
 * (629KB tek chunk) buradan geliyordu.
 */
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth }   from './context/AuthContext';
import { I18nProvider }            from './context/I18nContext';
import { ThemeProvider }           from './context/ThemeContext';
import { setCanonical } from './utils/seoMeta';
import LandingPage         from './pages/LandingPage';

const MainLayout          = lazy(() => import('./components/layout/MainLayout'));
const LoginPage           = lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage  = lazy(() => import('./pages/ForgotPasswordPage'));
const VerifyEmailPage     = lazy(() => import('./pages/VerifyEmailPage'));
const ResetPasswordPage   = lazy(() => import('./pages/ResetPasswordPage'));
const DashboardPage       = lazy(() => import('./pages/DashboardPage'));
const HabitDetailPage     = lazy(() => import('./pages/HabitDetailPage'));
const CreateHabitPage     = lazy(() => import('./pages/CreateHabitPage'));
const AchievementsPage    = lazy(() => import('./pages/AchievementsPage'));
const LegalPage           = lazy(() => import('./pages/legal/LegalPage'));
const BlogListPage        = lazy(() => import('./pages/blog/BlogListPage'));
const BlogPostPage        = lazy(() => import('./pages/blog/BlogPostPage'));
const StatisticsPage      = lazy(() => import('./pages/StatisticsPage'));
const AdminPage           = lazy(() => import('./pages/AdminPage'));
const SettingsPage        = lazy(() => import('./pages/SettingsPage'));
const EditHabitPage       = lazy(() => import('./pages/EditHabitPage'));
const HistoryPage         = lazy(() => import('./pages/HistoryPage'));
const DuelPage            = lazy(() => import('./pages/DuelPage'));

function PageFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen text-slate-400 dark:text-slate-500">
      Yükleniyor...
    </div>
  );
}

// Her rota için varsayılan olarak kendine referans veren bir canonical ayarlar
// (LandingPage ve LegalPage kendi canonical'larını ayrıca set eder — değeri aynı
// olduğundan çakışma yok, yalnızca onlar dışındaki tüm sayfaları da kapsar).
function CanonicalSync() {
  const { pathname } = useLocation();
  useEffect(() => {
    setCanonical(`https://surekly.com${pathname}`);
  }, [pathname]);
  return null;
}

// Davet linkiyle (surekly.com/?ref=<username>) girilirse kodu localStorage'a kaydeder —
// landing page'deki "Kayıt Ol" bağlantıları ?ref='i taşımadığından (bkz. LandingPage.jsx
// CTA'ları) LoginPage register isteğinde buradan okur. "İlk temas" mantığı: zaten bir
// kod kayıtlıysa üzerine yazmaz.
function ReferralCapture() {
  useEffect(() => {
    try {
      const ref = new URLSearchParams(window.location.search).get('ref');
      if (ref && !localStorage.getItem('surekly_ref')) localStorage.setItem('surekly_ref', ref);
    } catch { /* localStorage yoksa sessizce geç */ }
  }, []);
  return null;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageFallback />;
  return user ? children : <Navigate to="/login" replace />;
}

export default function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <CanonicalSync />
            <ReferralCapture />
            <Suspense fallback={<PageFallback />}>
              <Routes>
                {/* Ana sayfa — dil URL'den gelir, her biri ayrı bir hreflang sürümü (SEO) */}
                <Route path="/"               element={<LandingPage lang="tr" />} />
                <Route path="/en"             element={<LandingPage lang="en" />} />
                <Route path="/de"             element={<LandingPage lang="de" />} />
                <Route path="/fr"             element={<LandingPage lang="fr" />} />
                <Route path="/es"             element={<LandingPage lang="es" />} />
                <Route path="/pt"             element={<LandingPage lang="pt" />} />
                <Route path="/ru"             element={<LandingPage lang="ru" />} />
                <Route path="/login"          element={<LoginPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/verify-email"    element={<VerifyEmailPage />} />
                <Route path="/reset-password"  element={<ResetPasswordPage />} />
                <Route path="/gizlilik"        element={<LegalPage />} />
                <Route path="/kosullar"        element={<LegalPage />} />
                <Route path="/blog"            element={<BlogListPage lang="tr" />} />
                <Route path="/blog/:slug"      element={<BlogPostPage lang="tr" />} />
                <Route path="/en/blog"         element={<BlogListPage lang="en" />} />
                <Route path="/en/blog/:slug"   element={<BlogPostPage lang="en" />} />
                <Route path="/es/blog"         element={<BlogListPage lang="es" />} />
                <Route path="/es/blog/:slug"   element={<BlogPostPage lang="es" />} />
                <Route path="/de/blog"         element={<BlogListPage lang="de" />} />
                <Route path="/de/blog/:slug"   element={<BlogPostPage lang="de" />} />
                <Route path="/fr/blog"         element={<BlogListPage lang="fr" />} />
                <Route path="/fr/blog/:slug"   element={<BlogPostPage lang="fr" />} />
                <Route path="/pt/blog"         element={<BlogListPage lang="pt" />} />
                <Route path="/pt/blog/:slug"   element={<BlogPostPage lang="pt" />} />
                <Route path="/ru/blog"         element={<BlogListPage lang="ru" />} />
                <Route path="/ru/blog/:slug"   element={<BlogPostPage lang="ru" />} />
                <Route
                  element={
                    <ProtectedRoute>
                      <MainLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="/dashboard"  element={<DashboardPage />} />
                  <Route path="/habits/new" element={<CreateHabitPage />} />
                  <Route path="/habits/:id" element={<HabitDetailPage />} />
                  <Route path="/habits/:id/edit" element={<EditHabitPage />} />
                  <Route path="/history"        element={<HistoryPage />} />
                  <Route path="/duel/:code"  element={<DuelPage />} />
                  <Route path="/statistics" element={<StatisticsPage />} />
                  <Route path="/admin"      element={<AdminPage />} />
                  <Route path="/achievements" element={<AchievementsPage />} />
                  <Route path="/settings"   element={<SettingsPage />} />
                </Route>
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}
